import { Link } from 'react-router-dom'
import { getCollections, getProducts } from '../api/client'
import { CollectionGrid } from '../components/CollectionGrid'
import { Hero, Leitsatz, Zusagen } from '../components/Hero'
import { ProductGrid } from '../components/ProductGrid'
import { Fehlerhinweis, Leerhinweis, RasterSkelett } from '../components/States'
import { Zeichen } from '../components/Zeichen'
import { useResource } from '../hooks/useResource'
import { useSeo } from '../hooks/useSeo'
import { useT } from '../i18n'
import { useShopConfig } from '../shopConfig'

const AUF_DER_STARTSEITE = 8

/**
 * Die Startseite: Bühne, Zusagen, Sammlungen, Sortiment, und der Satz des
 * Ladens als Atempause dazwischen.
 */
export function HomePage() {
  const t = useT()
  const { shopName, primaryHost, theme } = useShopConfig()

  // Zwei getrennte Aufrufe statt eines gebündelten: Die Sammlungen stehen
  // meist vor den Produkten da, und dann ist schon die halbe Seite sichtbar.
  const sammlungen = useResource((signal) => getCollections(signal), [])
  const produkte = useResource(
    (signal) => getProducts({ pageSize: AUF_DER_STARTSEITE }, signal),
    [],
  )

  useSeo({
    title: t('home.seoTitel', { laden: shopName }),
    description: t('home.seoText', { laden: shopName }),
    canonicalHost: primaryHost,
  })

  // Der Leitsatz nur, wenn er nicht schon auf der Bühne steht: Ohne eigene
  // Unterzeile nimmt die Bühne ihn dort hinein, und derselbe Satz zweimal auf
  // einer Seite liest sich wie ein Fehler.
  const leitsatz = theme?.heroSubline?.trim() ? theme?.tagline?.trim() : ''

  return (
    <>
      <Hero theme={theme} shopName={shopName} />

      <Zusagen texte={theme?.promises ?? []} />

      <section className="sf-abschnitt" aria-labelledby="ueberschrift-sammlungen">
        <div className="sf-abschnitt-kopf">
          <h2 id="ueberschrift-sammlungen">{t('home.sammlungenTitel')}</h2>
          <Link className="sf-kapsel" to="/sammlung">
            {t('home.alleSammlungen')}
            <Zeichen art="pfeil" groesse={16} />
          </Link>
        </div>

        {sammlungen.loading && <Leerhinweis text={t('sammlung.laedt')} />}
        {sammlungen.error != null && <Fehlerhinweis fehler={sammlungen.error} />}
        {sammlungen.data?.length === 0 && <Leerhinweis text={t('sammlung.keine')} />}
        {sammlungen.data && sammlungen.data.length > 0 && (
          <CollectionGrid sammlungen={sammlungen.data.slice(0, 8)} />
        )}
      </section>

      <section className="sf-abschnitt" aria-labelledby="ueberschrift-produkte">
        <div className="sf-abschnitt-kopf">
          <h2 id="ueberschrift-produkte">{t('home.sortiment')}</h2>
          <Link className="sf-kapsel" to="/suche">
            {t('home.allesAnsehen')}
            <Zeichen art="pfeil" groesse={16} />
          </Link>
        </div>

        {produkte.loading && <RasterSkelett anzahl={AUF_DER_STARTSEITE} />}
        {produkte.error != null && <Fehlerhinweis fehler={produkte.error} />}
        {produkte.data?.products.length === 0 && <Leerhinweis text={t('produkt.keine')} />}
        {produkte.data && produkte.data.products.length > 0 && (
          <ProductGrid produkte={produkte.data.products} />
        )}
      </section>

      {leitsatz && (
        <section className="sf-abschnitt">
          <Leitsatz text={leitsatz} von={shopName} />
        </section>
      )}
    </>
  )
}
